<template>
  <div class="report">
    <!-- Controls teleported to header -->
    <Teleport to="#report-controls" v-if="controlsTarget">
      <slot name="controls"></slot>
    </Teleport>

    <!-- Section Header (optional) -->
    <div v-if="$slots.header" class="report-section-header">
      <slot name="header"></slot>
    </div>

    <!-- Report Content -->
    <div class="report-grid" ref="contentContainer" :style="gridStyle">
      <slot name="content"></slot>
    </div>

    <!-- Loading Overlay -->
    <div v-if="loading" class="loading-overlay" :style="loadingOverlayStyle">
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script>
import { jsPDF } from 'jspdf';

export default {
  name: 'BaseReport',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    reportTitle: {
      type: String,
      default: 'Report'
    },
    reportSubtitle: {
      type: String,
      default: ''
    },
    columns: {
      type: Number,
      default: 4
    }
  },
  emits: ['mounted'],
  data() {
    return {
      controlsTarget: false,
      loadingPosition: {
        top: 0,
        left: 0,
        width: 0,
        height: 0
      }
    };
  },
  computed: {
    loadingOverlayStyle() {
      return {
        position: 'fixed',
        top: `${this.loadingPosition.top}px`,
        left: `${this.loadingPosition.left}px`,
        width: `${this.loadingPosition.width}px`,
        height: `${this.loadingPosition.height}px`
      };
    },
    gridStyle() {
      return {
        '--grid-columns': this.columns
      };
    }
  },
  mounted() {
    this.controlsTarget = !!document.getElementById('report-controls');
    this.updateLoadingPosition();
    window.addEventListener('resize', this.updateLoadingPosition);
    this.$emit('mounted');
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateLoadingPosition);
  },
  methods: {
    updateLoadingPosition() {
      const container = this.$refs.contentContainer;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      this.loadingPosition = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      };
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },

    formatNumber(value) {
      return new Intl.NumberFormat('en-US').format(value);
    },

    formatPercent(value) {
      return `${value.toFixed(1)}%`;
    },

    /**
     * Generate PDF from report content
     * @param {Object} options - PDF generation options
     * @param {Array} options.stats - Array of { label, value, change } for KPI stats
     * @param {Array} options.chartTitles - Array of chart titles in order
     * @returns {Promise<Blob>} PDF blob
     */
    async getPDF(options = {}) {
      const { stats = [], chartTitles = [] } = options;

      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 32;
      const contentWidth = pageWidth - margin * 2;
      let y = 0;

      // Header - full width, primary color
      doc.setFillColor(67, 56, 202);
      doc.rect(0, 0, pageWidth, 52, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text(this.reportTitle, margin, 22);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(200, 200, 255);
      doc.text(this.reportSubtitle || '', margin, 38);
      y = 68;

      // Stats section
      if (stats.length > 0) {
        stats.forEach((stat) => {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(9);
          doc.setTextColor(100, 100, 100);
          doc.text(stat.label, margin, y + 8);
          y += 13;

          doc.setFont('helvetica', 'normal');
          doc.setFontSize(12);
          doc.setTextColor(38, 38, 38);
          doc.text(String(stat.value), margin, y + 8);

          if (stat.change !== null && stat.change !== undefined) {
            const valueWidth = doc.getTextWidth(String(stat.value));
            doc.setFontSize(8);
            doc.setTextColor(stat.change >= 0 ? 22 : 220, stat.change >= 0 ? 163 : 38, stat.change >= 0 ? 74 : 38);
            doc.text(` (${stat.change >= 0 ? '+' : ''}${stat.change}%)`, margin + valueWidth + 4, y + 8);
          }
          y += 22;
        });
        y += 8;
      }

      // Charts
      const canvases = this.$refs.contentContainer.querySelectorAll('canvas');
      const chartHeight = 100;

      for (let i = 0; i < canvases.length; i++) {
        const neededHeight = chartHeight + 28;
        if (y + neededHeight > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }

        // Chart title
        const chartTitle = chartTitles[i] || `Chart ${i + 1}`;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.text(chartTitle, margin, y + 8);
        y += 16;

        // Chart image
        const canvas = canvases[i];
        const imgData = canvas.toDataURL('image/png', 1.0);
        const aspectRatio = canvas.width / canvas.height;
        const imgHeight = chartHeight;
        const imgWidth = Math.min(imgHeight * aspectRatio, contentWidth);
        doc.addImage(imgData, 'PNG', margin, y, imgWidth, imgHeight);
        y += chartHeight + 12;
      }

      return doc.output('blob');
    },

    /**
     * Generate a report PDF with KPI cards and table (no charts).
     * Follows the same visual style as DailyReport's generatePDF().
     * @param {Object} options
     * @param {string} options.title - Report title
     * @param {string} options.subtitle - Report subtitle (e.g. date range)
     * @param {Array} options.kpis - Array of { label, value, format } where format is 'currency' or 'number'
     * @param {Array} options.columns - Table column definitions [{ key, label, format }]
     * @param {Array} options.rows - Table data rows
     * @param {Object} options.summaryRow - Optional summary row object
     */
    async generateReportPDF({ title, subtitle, kpis = [], columns = [], rows = [], summaryRow = null }) {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;

      // Load Rubik font from CDN
      let fontFamily = 'helvetica';
      try {
        const fontUrl = 'https://cdn.jsdelivr.net/fontsource/fonts/rubik@latest/latin-400-normal.ttf';
        const fontResponse = await fetch(fontUrl);
        if (!fontResponse.ok) throw new Error('Failed to fetch regular font');
        const fontBuffer = await fontResponse.arrayBuffer();
        const fontBytes = new Uint8Array(fontBuffer);
        let fontBase64 = '';
        for (let i = 0; i < fontBytes.length; i++) {
          fontBase64 += String.fromCharCode(fontBytes[i]);
        }
        fontBase64 = btoa(fontBase64);
        doc.addFileToVFS('Rubik-Regular.ttf', fontBase64);
        doc.addFont('Rubik-Regular.ttf', 'Rubik', 'normal');

        const fontBoldUrl = 'https://cdn.jsdelivr.net/fontsource/fonts/rubik@latest/latin-700-normal.ttf';
        const fontBoldResponse = await fetch(fontBoldUrl);
        if (!fontBoldResponse.ok) throw new Error('Failed to fetch bold font');
        const fontBoldBuffer = await fontBoldResponse.arrayBuffer();
        const fontBoldBytes = new Uint8Array(fontBoldBuffer);
        let fontBoldBase64 = '';
        for (let i = 0; i < fontBoldBytes.length; i++) {
          fontBoldBase64 += String.fromCharCode(fontBoldBytes[i]);
        }
        fontBoldBase64 = btoa(fontBoldBase64);
        doc.addFileToVFS('Rubik-Bold.ttf', fontBoldBase64);
        doc.addFont('Rubik-Bold.ttf', 'Rubik', 'bold');

        fontFamily = 'Rubik';
      } catch (fontErr) {
        console.warn('Failed to load Rubik font, falling back to helvetica:', fontErr);
        fontFamily = 'helvetica';
      }

      // Header
      doc.setFillColor(248, 248, 248);
      doc.rect(0, 0, pageWidth, 60, 'F');
      doc.setFont(fontFamily, 'bold');
      doc.setFontSize(18);
      doc.setTextColor(0, 0, 0);
      doc.text(title || this.reportTitle, margin, 30);
      doc.setFont(fontFamily, 'normal');
      doc.setFontSize(12);
      doc.setTextColor(80, 80, 80);
      doc.text(subtitle || this.reportSubtitle || '', margin, 48);

      let y = 80;

      // KPI cards
      if (kpis.length > 0) {
        const kpiWidth = (pageWidth - margin * 2 - (kpis.length - 1) * 10) / kpis.length;
        kpis.forEach((kpi, index) => {
          const x = margin + index * (kpiWidth + 10);
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(1);
          doc.rect(x, y, kpiWidth, 50, 'S');
          doc.setFont(fontFamily, 'normal');
          doc.setFontSize(8);
          doc.setTextColor(60, 60, 60);
          doc.text(kpi.label, x + 10, y + 15);
          doc.setFont(fontFamily, 'bold');
          doc.setFontSize(14);
          doc.setTextColor(0, 0, 0);
          let formattedValue;
          if (kpi.format === 'currency') {
            formattedValue = new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2
            }).format(kpi.value);
          } else {
            formattedValue = new Intl.NumberFormat('en-US').format(kpi.value);
          }
          doc.text(formattedValue, x + 10, y + 38);
        });
        y += 70;
      }

      // Table
      if (columns.length > 0) {
        const tableWidth = pageWidth - margin * 2;
        const numCols = columns.length;
        const colWidth = tableWidth / numCols;
        const colWidths = Array(numCols).fill(colWidth);
        const rowHeight = 16;

        // Helper to clip text with ellipsis
        const clipText = (text, maxWidth) => {
          text = String(text);
          if (doc.getTextWidth(text) <= maxWidth) return text;
          let clipped = text;
          while (clipped.length > 0 && doc.getTextWidth(clipped + '...') > maxWidth) {
            clipped = clipped.slice(0, -1);
          }
          return clipped + '...';
        };

        // Format cell value based on column format
        const formatCell = (value, col) => {
          if (value === null || value === undefined) return '—';
          if (col.format === 'currency') {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
          }
          if (col.format === 'number') {
            return new Intl.NumberFormat('en-US').format(value);
          }
          return String(value);
        };

        // Header row
        doc.setFillColor(248, 248, 248);
        doc.rect(margin, y, tableWidth, rowHeight, 'F');
        doc.setFont(fontFamily, 'bold');
        doc.setFontSize(6.5);
        doc.setTextColor(0, 0, 0);

        let x = margin;
        columns.forEach((col, i) => {
          doc.text(clipText(col.label, colWidths[i] - 8), x + 4, y + 10);
          x += colWidths[i];
        });
        y += rowHeight;

        // Data rows
        doc.setFont(fontFamily, 'normal');
        doc.setFontSize(6);
        doc.setTextColor(0, 0, 0);

        rows.forEach((row) => {
          if (y + rowHeight > pageHeight - margin) {
            doc.addPage();
            y = margin;
          }

          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.5);
          doc.line(margin, y + rowHeight, margin + tableWidth, y + rowHeight);

          x = margin;
          columns.forEach((col, i) => {
            const cellMaxWidth = colWidths[i] - 8;
            const text = clipText(formatCell(row[col.key], col), cellMaxWidth);
            doc.text(text, x + 4, y + 10);
            x += colWidths[i];
          });
          y += rowHeight;
        });

        // Summary row
        if (summaryRow) {
          if (y + rowHeight > pageHeight - margin) {
            doc.addPage();
            y = margin;
          }

          doc.setFillColor(240, 240, 240);
          doc.rect(margin, y, tableWidth, rowHeight, 'F');
          doc.setFont(fontFamily, 'bold');

          x = margin;
          columns.forEach((col, i) => {
            const cellMaxWidth = colWidths[i] - 8;
            const text = clipText(formatCell(summaryRow[col.key], col), cellMaxWidth);
            doc.text(text, x + 4, y + 10);
            x += colWidths[i];
          });
        }
      }

      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
    },

    getContentContainer() {
      return this.$refs.contentContainer;
    }
  }
}
</script>

<style scoped>
.report {
  height: 100%;
  width: 100%;
}

.report-section-header {
  padding: 32px 24px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
}

.report-section-header :deep(h2) {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #262626;
}

.report-section-header :deep(p) {
  margin: 8px 0 0 0;
  font-size: 15px;
  color: #6b7280;
}

/* Grid Layout - Default: stacked vertically (used for PDF export) */
.report-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1px;
  background: #e5e7eb;
}

.report-grid > :deep(*) {
  background: #fff;
}

/* Desktop: configurable column grid layout */
@media (min-width: 1024px) {
  .report-grid {
    grid-template-columns: repeat(var(--grid-columns, 4), 1fr);
  }
}

/* Loading overlay */
.loading-overlay {
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  z-index: 10;
  pointer-events: none;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f4f6;
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
